import { getLcToUsdRate, getPaid, getRequested } from '../helpers/sumActivities'
import {
  IMdfActivityContribution,
  IMdfActivityWithBusinessPriorities,
  TMdfAllocationEntityKey,
  TMdfBreakdownGroupField,
} from '../model/types'

import { parseSeparatedString } from './parseSeparatedString'

const EMPTY_GROUP_LABEL = '—'

/**
 * Group fields that also exist as a column of the activity's category split.
 * Those are bucketed per allocation; the rest (`businessPriorities`, `activityType`,
 * `timeframe`) have no counterpart in the split and keep the whole-activity bucketing.
 */
const GROUP_FIELD_TO_ALLOCATION_KEY = {
  strategicInitiatives: 'strategicInitiative',
  businessUnit: 'businessUnit',
  category: 'category',
  businessFocus: 'businessFocus',
} as const satisfies Partial<Record<TMdfBreakdownGroupField, TMdfAllocationEntityKey>>

type TAllocationGroupField = keyof typeof GROUP_FIELD_TO_ALLOCATION_KEY

const getAllocationKey = (
  groupField: TMdfBreakdownGroupField
): TMdfAllocationEntityKey | undefined =>
  GROUP_FIELD_TO_ALLOCATION_KEY[groupField as TAllocationGroupField]

/**
 * Bucket label for an activity with no usable split: the legacy field stringified,
 * as before. `category` needs parsing first — it is the one legacy list the mapper
 * leaves as the raw `";OPS;HPS"` API string instead of a parsed array.
 *
 * The legacy lists repeat a value once per allocation row (`";Print;Print;Print"`), so repeats
 * are collapsed — the same rule the activities table and the details modal apply, which was
 * missing here and turned one business unit into a `Print,Print,Print` bucket.
 */
const getLegacyGroupLabel = (
  activity: IMdfActivityWithBusinessPriorities,
  groupField: TMdfBreakdownGroupField
): string => {
  const value =
    groupField === 'category' ? parseSeparatedString(activity.category) : activity[groupField]

  if (Array.isArray(value)) {
    return Array.from(new Set(value)).join(',') || EMPTY_GROUP_LABEL
  }

  return String(value ?? EMPTY_GROUP_LABEL) || EMPTY_GROUP_LABEL
}

/**
 * How much of one activity lands in which bucket of the selected group field — the shared
 * primitive behind both the breakdown chart's totals ({@link getMdfBreakdownGroups}) and the
 * cockpit tile's grouped activity list, so the money rules below are stated exactly once.
 *
 * When the group field has a counterpart in the activity's category split, the activity is
 * spread across its allocations: an activity split between `Managed` and `Transactional Toner`
 * contributes to both buckets, instead of landing in a single stringified
 * `"Managed,Transactional Toner"` one. Nothing is then read from the legacy `;`-separated
 * fields — the split is the only source.
 *
 * Amounts come from the split where it has them and are apportioned where it does not:
 * - requested — `categoryAllocationLc`, which sums to the activity's `requestedAmountLc`,
 *   converted with {@link getLcToUsdRate} for the USD view since the split is reported in local
 *   currency only;
 * - paid — the split carries no paid amount, so the activity's `claimPaid*` is apportioned by
 *   `categoryAllocationPercent`.
 *
 * Activities with no usable split keep the whole-activity bucketing, so no money is lost and
 * both currency views agree. Activities carrying no money at all contribute nothing — see the
 * note on the check below.
 */
export const getMdfActivityContributions = (
  activity: IMdfActivityWithBusinessPriorities,
  groupField: TMdfBreakdownGroupField,
  isUsd: boolean
): IMdfActivityContribution[] => {
  const requested = getRequested(activity, isUsd)
  const paid = getPaid(activity, isUsd)

  // Cancelled activities keep their planned split after both totals are zeroed out. They add
  // nothing to a requested-vs-paid view, but bucketing them would still raise an empty bar
  // under a stringified legacy label, so they are dropped.
  if (!requested && !paid) {
    return []
  }

  const allocationKey = getAllocationKey(groupField)
  const allocations = allocationKey ? (activity.strategicInitiativeAllocations ?? []) : []

  // A split with no requested amount behind it no longer ties to the activity and gives no USD
  // rate, so it is not used — as for records with no split at all.
  const isSplitUsable = Boolean(allocations.length) && getRequested(activity, false) > 0

  if (!allocationKey || !isSplitUsable) {
    return [{ name: getLegacyGroupLabel(activity, groupField), requested, paid }]
  }

  const lcToUsd = isUsd ? getLcToUsdRate(activity) : 1

  return allocations.map((allocation) => ({
    name: allocation[allocationKey] || EMPTY_GROUP_LABEL,
    requested: allocation.categoryAllocationLc * lcToUsd,
    paid: (paid * allocation.categoryAllocationPercent) / 100,
  }))
}
